import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HelpService } from './help.service';
import { HelpArticleSummary } from './models/help-article.model';
import { GENERAL_COLLECTION, helpIcon } from './help-icon';

interface CategorySection {
  category: string;
  articles: HelpArticleSummary[];
}

/**
 * A single collection (top-level Section): header + standalone articles + Category sub-sections.
 * Navigation-agnostic — emits [openArticle] and [back].
 */
@Component({
  selector: 'ee-help-collection',
  templateUrl: './help-collection.component.html',
  styleUrls: ['./help-collection.component.scss'],
  standalone: false,
})
export class HelpCollectionComponent {
  @Output() openArticle = new EventEmitter<string>();
  @Output() back = new EventEmitter<void>();

  loading = true;
  error = false;
  icon = '';
  total = 0;
  standalone: HelpArticleSummary[] = [];
  sections: CategorySection[] = [];

  private current = '';

  @Input()
  set collection(value: string) {
    this.current = value ?? '';
    this.icon = helpIcon(this.current);
    this.load();
  }

  get collection(): string {
    return this.current;
  }

  constructor(private helpService: HelpService) {}

  private load(): void {
    this.loading = true;
    this.error = false;
    this.helpService.list().subscribe({
      next: (articles) => {
        this.build(articles.filter((a) => (a.section || GENERAL_COLLECTION) === this.current));
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  private build(articles: HelpArticleSummary[]): void {
    this.total = articles.length;
    this.standalone = articles.filter((a) => !a.category);

    const byCategory = new Map<string, HelpArticleSummary[]>();
    for (const article of articles) {
      if (!article.category) {
        continue;
      }
      const bucket = byCategory.get(article.category) ?? [];
      bucket.push(article);
      byCategory.set(article.category, bucket);
    }
    this.sections = Array.from(byCategory.entries())
      .map(([category, items]) => ({ category, articles: items }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }
}
